/**
 * @fileoverview OptimizationResultSyncer - Syncs completed optimization queue results to BacktestContext
 * This component handles the integration between the optimization queue and Key Metrics display
 * Must be placed within a BacktestProvider to function properly
 * 
 * Note: Since completed jobs are now stored in database and removed from memory,
 * this component listens for completion events and captures the results before storage.
 */

import React, { useContext, useEffect, useRef } from 'react';
import { OptimizationQueueContext } from '../../contexts/OptimizationQueueContext';
import { useBacktestContext } from '../../contexts/BacktestContext';
import { optimizationEventService } from '../../services/optimization/OptimizationEventService';
import type { OptimizationEvent } from '../../services/optimization/OptimizationEventService';
import { optimizationDebugLog } from '../../utils/optimizationDebugLogger';
import { logToFile } from '../../utils/debugLogger';

export const OptimizationResultSyncer: React.FC = () => {
    const queueContext = useContext(OptimizationQueueContext);

    if (!queueContext) {
        optimizationDebugLog('OptimizationResultSyncer', 'Context provider not found, skipping syncer render', undefined, { console: false });
        return null;
    }

    const {
        activeJobs,
        queuedJobs,
        completedJobs,
        selectedJob,
        selectedJobId
    } = queueContext;
    const { setOptimizationResults } = useBacktestContext();

    // Track synced jobs to prevent infinite loops
    const syncedJobsRef = useRef<Set<string>>(new Set());
    const appliedJobIdRef = useRef<string | null>(null);

    optimizationDebugLog('OptimizationResultSyncer', 'Render', {
        completedJobs: completedJobs.length,
        syncedJobs: syncedJobsRef.current.size
    }, { console: false });



    // Check for already completed jobs and sync their results immediately
    useEffect(() => {
        optimizationDebugLog('OptimizationResultSyncer', 'Completed job IDs from queue', completedJobs.map(job => job.id), { console: false });

        completedJobs.forEach(job => {
            // Prevent syncing the same job multiple times
            if (syncedJobsRef.current.has(job.id)) {
                optimizationDebugLog('OptimizationResultSyncer', 'Job already synced, skipping', { jobId: job.id }, { console: false });
                return;
            }

            if (!job.bestResult) {
                optimizationDebugLog('OptimizationResultSyncer', 'Completed job missing bestResult', { jobId: job.id }, { console: false });
                return;
            }

            optimizationDebugLog('OptimizationResultSyncer', 'Syncing completed job results from queue', { jobId: job.id }, { console: false });

            // Mark job as synced
            syncedJobsRef.current.add(job.id);

            // Inline the sync logic to avoid dependency on syncJobResults
            const metrics = job.bestResult.metrics || {};
            optimizationDebugLog('OptimizationResultSyncer', 'Available metrics from completed job', {
                jobId: job.id,
                metricKeys: Object.keys(metrics),
                metrics
            }, { console: false });

            // DEBUG: Log the raw metrics to understand what's coming from backend

            const mockBacktestResult = {
                stats: {
                    // Core metrics
                    // Note: metrics.totalReturn from optimization is already a percentage (e.g., 725.27 for 725.27%)
                    totalReturn: metrics.totalReturn || 0,
                    totalReturnPercent: metrics.totalReturnPercent || metrics.totalReturn || 0,
                    sharpeRatio: metrics.sharpeRatio || 0,
                    winRate: metrics.winRate || 0,
                    // Note: metrics.maxDrawdown from optimization is a decimal (e.g., 0.46 for 46%)
                    maxDrawdown: metrics.maxDrawdown || 0,
                    maxDrawdownPercent: metrics.maxDrawdownPercent || metrics.maxDrawdown || 0,

                    // Trade metrics
                    totalTrades: metrics.totalTrades || metrics.tradeCount || 0,
                    tradeCount: metrics.totalTrades || metrics.tradeCount || 0,
                    wins: metrics.wins || 0,
                    losses: metrics.losses || 0,

                    // Performance metrics
                    profitFactor: metrics.profitFactor || 0,
                    totalProfit: metrics.totalProfit || 0,
                    totalFees: metrics.totalFees || 0,
                    profitPercent: metrics.profitPercent || 0,

                    // Additional metrics that might be present
                    avgEntry: metrics.avgEntry || 0,
                    avgExit: metrics.avgExit || 0,
                    quantity: metrics.quantity || 0,
                    avgTradeLength: metrics.avgTradeLength || 0,
                    sharpeRatioPerTrade: metrics.sharpeRatioPerTrade || 0,
                },
                trades: job.bestResult.trades || [],
                signals: job.bestResult.signals || [],
                equity: job.bestResult.equity || []
            };

            const contextOptimizationResults = {
                bestResult: mockBacktestResult,
                allResults: job.allResults || [], // Use allResults from job for heatmap
                parameterRanges: job.parameterRanges, // Include parameter ranges for heatmap
                optimizationTarget: job.optimizationTarget, // Include optimization target
                candleRangeMetadata: job.candleRangeMetadata, // CRITICAL: Include candle range metadata for save
                parameters: job.bestResult.parameters || {},
                optimizationMetrics: {
                    totalCombinations: job.total || 0,
                    completedCombinations: job.current || 0,
                    bestPerformance: job.bestResult.score || 0,
                    averagePerformance: job.bestResult.score || 0
                },
                jobId: job.id // Include job ID for linking saved results
            };

            const syncerInfo = {
                jobId: job.id,
                allResultsCount: contextOptimizationResults.allResults?.length || 0,
                jobHasAllResults: !!(job.allResults),
                jobAllResultsLength: job.allResults?.length || 0,
                allResultsSample: contextOptimizationResults.allResults?.slice(0, 2), // First 2 items for verification
                hasCandleRangeMetadata: !!contextOptimizationResults.candleRangeMetadata,
                candleRangeMetadata: contextOptimizationResults.candleRangeMetadata
            };

            logToFile('🔍 [OptimizationResultSyncer] Setting contextOptimizationResults', syncerInfo);

            appliedJobIdRef.current = job.id;
            optimizationDebugLog('OptimizationResultSyncer', 'Applied completed job results to BacktestContext', {
                jobId: job.id,
                stats: mockBacktestResult.stats,
                parameters: job.bestResult.parameters,
                optimizationScore: job.bestResult.score
            }, { console: false });

            setOptimizationResults(contextOptimizationResults);
        });
    }, [completedJobs]); // Need full dependency but with proper tracking

    useEffect(() => {
        if (!selectedJob || !selectedJob.bestResult) {
            return;
        }

        if (appliedJobIdRef.current === selectedJob.id) {
            return;
        }

        // Skip WFA jobs - they are handled by WfaResultSyncer
        if (selectedJob.bestResult?.type === 'wfa') {
            return;
        }

        optimizationDebugLog('OptimizationResultSyncer', 'Applying selected optimization job to BacktestContext', { jobId: selectedJob.id }, { console: false });

        const metrics = selectedJob.bestResult.metrics || {};

        const mockBacktestResult = {
            stats: {
                // Core metrics
                // Note: metrics.totalReturn from optimization is already a percentage (e.g., 725.27 for 725.27%)
                totalReturn: metrics.totalReturn || 0,
                totalReturnPercent: metrics.totalReturnPercent || metrics.totalReturn || 0,
                sharpeRatio: metrics.sharpeRatio || 0,
                winRate: metrics.winRate || 0,
                // Note: metrics.maxDrawdown from optimization is a decimal (e.g., 0.46 for 46%)
                maxDrawdown: metrics.maxDrawdown || 0,
                maxDrawdownPercent: metrics.maxDrawdownPercent || metrics.maxDrawdown || 0,
                // Trade metrics
                totalTrades: metrics.totalTrades || metrics.tradeCount || 0,
                tradeCount: metrics.totalTrades || metrics.tradeCount || 0,
                wins: metrics.wins || 0,
                losses: metrics.losses || 0,
                // Performance metrics
                profitFactor: metrics.profitFactor || 0,
                totalProfit: metrics.totalProfit || 0,
                totalFees: metrics.totalFees || 0,
                profitPercent: metrics.profitPercent || 0,
                // Additional metrics
                avgEntry: metrics.avgEntry || 0,
                avgExit: metrics.avgExit || 0,
                quantity: metrics.quantity || 0,
                avgTradeLength: metrics.avgTradeLength || 0,
                sharpeRatioPerTrade: metrics.sharpeRatioPerTrade || 0,
            },
            trades: selectedJob.bestResult.trades || [],
            signals: selectedJob.bestResult.signals || [],
            equity: selectedJob.bestResult.equity || []
        };

        const contextOptimizationResults = {
            bestResult: mockBacktestResult,
            allResults: selectedJob.allResults || [], // Use allResults from selected job for heatmap
            parameters: selectedJob.bestResult.parameters || {},
            optimizationMetrics: {
                totalCombinations: selectedJob.total || 0,
                completedCombinations: selectedJob.current || 0,
                bestPerformance: selectedJob.bestResult.score || 0,
                averagePerformance: selectedJob.bestResult.score || 0
            },
            jobId: selectedJob.id // Include job ID for linking saved results
        };

        setOptimizationResults(contextOptimizationResults);
        appliedJobIdRef.current = selectedJob.id;
    }, [selectedJobId, selectedJob, setOptimizationResults]);

    // Listen for optimization completion events and sync to BacktestContext before jobs are stored to DB
    useEffect(() => {
        const unsubscribers: (() => void)[] = [];

        // Subscribe to completion events for all active jobs
        [...activeJobs, ...queuedJobs].forEach(job => {
            const unsubscribe = optimizationEventService.subscribe(job.id, (event: OptimizationEvent) => {
                if ((event.type === 'complete' || event.type === 'completed') && 'parameters' in event.data) {
                    // Prevent syncing the same job multiple times
                    if (syncedJobsRef.current.has(event.jobId)) {
                        optimizationDebugLog('OptimizationResultSyncer', 'Completed event already synced, skipping', { jobId: event.jobId }, { console: false });
                        return;
                    }

                    optimizationDebugLog('OptimizationResultSyncer', 'Received completion event', event, { console: false });

                    // Mark job as synced
                    syncedJobsRef.current.add(event.jobId);

                    const completedData = event.data as {
                        parameters: Record<string, number>;
                        score: number;
                        metrics: Record<string, number>;
                        total?: number
                    };

                    // Convert to BacktestContext format with comprehensive metrics
                    const metrics = completedData.metrics || {};

                    const mockBacktestResult = {
                        stats: {
                            // Core metrics
                            // Note: metrics.totalReturn from optimization is already a percentage (e.g., 725.27 for 725.27%)
                            totalReturn: metrics.totalReturn || 0,
                            totalReturnPercent: metrics.totalReturnPercent || metrics.totalReturn || 0,
                            sharpeRatio: metrics.sharpeRatio || 0,
                            winRate: metrics.winRate || 0,
                            // Note: metrics.maxDrawdown from optimization is a decimal (e.g., 0.46 for 46%)
                            maxDrawdown: metrics.maxDrawdown || 0,
                            maxDrawdownPercent: metrics.maxDrawdownPercent || metrics.maxDrawdown || 0,

                            // Trade metrics
                            totalTrades: metrics.totalTrades || metrics.tradeCount || 0,
                            tradeCount: metrics.totalTrades || metrics.tradeCount || 0,
                            wins: metrics.wins || 0,
                            losses: metrics.losses || 0,

                            // Performance metrics
                            profitFactor: metrics.profitFactor || 0,
                            totalProfit: metrics.totalProfit || 0,
                            totalFees: metrics.totalFees || 0,
                            profitPercent: metrics.profitPercent || 0,

                            // Additional metrics
                            avgEntry: metrics.avgEntry || 0,
                            avgExit: metrics.avgExit || 0,
                            quantity: metrics.quantity || 0,
                            avgTradeLength: metrics.avgTradeLength || 0,
                            sharpeRatioPerTrade: metrics.sharpeRatioPerTrade || 0
                        },
                        trades: [], // Empty since queue doesn't store full trade data
                        signals: [], // Empty since queue doesn't store signal data  
                        equity: [] // Empty since queue doesn't store equity curve
                    };

                    const contextOptimizationResults = {
                        bestResult: mockBacktestResult,
                        allResults: (completedData as any).allResults || [], // Extract allResults from completion event
                        parameterRanges: (event.data as any).parameterRanges, // Include parameter ranges for heatmap
                        optimizationTarget: (event.data as any).optimizationTarget, // Include optimization target
                        candleRangeMetadata: (event.data as any).candleRangeMetadata, // CRITICAL: Include candle range metadata for save
                        parameters: completedData.parameters || {},
                        optimizationMetrics: {
                            totalCombinations: completedData.total || 0,
                            completedCombinations: completedData.total || 0,
                            bestPerformance: completedData.score || 0,
                            averagePerformance: completedData.score || 0
                        },
                        jobId: event.jobId // Include job ID for linking saved results
                    };

                    setOptimizationResults(contextOptimizationResults);

                    optimizationDebugLog('OptimizationResultSyncer', 'Applied completion event results to BacktestContext', {
                        jobId: event.jobId,
                        parameters: completedData.parameters,
                        optimizationScore: completedData.score
                    }, { console: false });
                    appliedJobIdRef.current = event.jobId;
                }
            });

            unsubscribers.push(unsubscribe);
        });

        return () => {
            unsubscribers.forEach(unsubscribe => unsubscribe());
        };
    }, [activeJobs.length, queuedJobs.length]); // Only depend on lengths to prevent infinite loops

    // This component doesn't render anything - it's purely for side effects
    return null;
};